import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STATES, VERBS } from './constants';
import createStore from './createStore';
import RRRFetch from './fetch';
import { changeMeta, getKey } from './utils';
const createGetAPI = (options) => {
    const { store, addListener, removeListener } = createStore();
    const { getURL, params } = options;
    const isProcessing = (id, query) => {
        const qs = getKey({ id, query });
        return store[qs]?.isProcessing;
    };
    const getData = async (opts) => {
        const { id, query, meta, abortSignal } = opts;
        const raw = false;
        const qs = getKey({ id, query });
        store[qs] = {
            ...store[qs],
            ...changeMeta({ state: STATES.PENDING, verb: VERBS.GET, code: -1 }),
        };
        try {
            const { code, data, permissions } = await RRRFetch.fetch(getURL(id, query, meta), {
                method: 'GET',
                signal: abortSignal,
                headers: {
                    ...(raw
                        ? {}
                        : {
                            'Content-Type': 'application/JSON',
                        }),
                    ...(params?.headers || {}),
                },
            });
            store[qs] = {
                ...store[qs],
                data,
                permissions,
                ...changeMeta({ state: STATES.SYNCED, verb: VERBS.GET, code }),
            };
            return store[qs];
        }
        catch (e) {
            const { code, error } = e;
            store[qs] = {
                ...store[qs],
                data: undefined,
                permissions: undefined,
                ...changeMeta({
                    state: STATES.FAILED,
                    verb: VERBS.GET,
                    code,
                    error,
                }),
            };
            return store[qs];
        }
    };
    const useGet = (initialId, initialQuery) => {
        const ref = useRef(initialId
            ? {
                id: initialId,
                query: initialQuery,
            }
            : null);
        const abortControllerRef = useRef();
        const [resource, setResource] = useState(ref.current ? store[getKey(ref.current)] : undefined);
        const get = useCallback(async (...args) => {
            const [id, query, meta] = args;
            ref.current = {
                id,
                query,
            };
            if (!isProcessing(id, query)) {
                abortControllerRef.current = new AbortController();
                await getData({
                    id,
                    query,
                    meta,
                    abortSignal: abortControllerRef.current.signal,
                });
                abortControllerRef.current = undefined;
            }
        }, []);
        const listener = useCallback((prop, value) => {
            if (ref.current && prop === getKey(ref.current)) {
                setResource(value);
            }
        }, []);
        useMemo(() => addListener(listener), [listener]);
        useEffect(() => {
            return () => {
                removeListener(listener);
            };
        }, [listener]);
        useEffect(() => {
            return () => {
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
            };
        }, []);
        return {
            get,
            resource,
            data: resource?.data,
            permissions: resource?.permissions,
            isProcessing: resource?.isProcessing || false,
            isProcessed: resource?.isProcessed || false,
            isProcessingFailed: resource?.isProcessingFailed || false,
            isStale: resource?.isStale || false,
            error: resource?.error,
        };
    };
    const invalidate = (id, query, allIdsInvalid = false) => {
        if (!allIdsInvalid) {
            const qs = getKey({ id, query });
            const response = store[qs];
            if (response) {
                store[qs] = {
                    ...response,
                    __STATE__: STATES.STALE,
                    isStale: true,
                };
            }
        }
        else {
            const keyList = Object.keys(store).filter(key => key.startsWith(id));
            keyList.forEach(key => {
                const response = store[key];
                if (response) {
                    store[key] = {
                        ...response,
                        __STATE__: STATES.STALE,
                        isStale: true,
                    };
                }
            });
        }
    };
    return {
        get: getData,
        useGet,
        invalidate,
    };
};
export default createGetAPI;
//# sourceMappingURL=createGetAPI.js.map